import { useEffect, useState } from 'react';

const nodeToString = (children) => {
    if (typeof children === 'string') {
        return children;
    }
    else if (Array.isArray(children)) {
        return children.map(nodeToString).join('');
    }
    else {
        console.warn('react-meta-elements: Encountered unknown element, ignoring.');
        return '';
    }
};

/**
 * Create a `<meta>` element and append it to the head.
 * @private
 */
const spawnHeadElement = (name) => {
    // Create the element
    const el = document.createElement(name);
    // Append into <head>
    const heads = document.getElementsByTagName('head');
    const head = heads && heads[0];
    if (head)
        head.appendChild(el);
    return el;
};

const ALIASES = {
    httpEquiv: 'http-equiv',
    useCredentials: 'use-credentials'
};
/**
 * If `value` is given, sets an attribute. If it's null'ish, the attribute is removed.
 * @private
 *
 * @example
 *     // set the attribute on:
 *     setOrRemoveAttribute(el, 'name', 'Name goes here')
 *
 *     // removes the attribute on any of these:
 *     setOrRemoveAttribute(el, 'name')
 *     setOrRemoveAttribute(el, 'name', null)
 *     setOrRemoveAttribute(el, 'name', undefined)
 */
const setOrRemoveAttribute = (el, name, value) => {
    // Normalize the name to account for React-like names (eg, useCredentials)
    const attrib = ALIASES[name] || name;
    if (value != null) {
        el.setAttribute(attrib, value);
    }
    else {
        el.removeAttribute(attrib);
    }
};

/**
 * React hook to set the document title.
 */
const useTitle = (title) => {
    useEffect(() => {
        if (title == null)
            return;
        const oldTitle = document.title;
        document.title = title;
        return () => {
            document.title = oldTitle;
        };
    }, [title]);
};
/**
 * React hook to insert a meta tag.
 */
const useMeta = (props) => {
    return useHeadElement(props, 'meta', [
        'charset',
        'content',
        'httpEquiv',
        'name',
        'property'
    ]);
};
const useLink = (props) => {
    return useHeadElement(props, 'link', [
        'as',
        'crossorigin',
        'useCredentials',
        'rel',
        'href',
        'hreflang',
        'media',
        'rel',
        'sizes',
        'title',
        'type'
    ]);
};
/**
 * React hook to insert an arbitrary head element.
 */
const useHeadElement = (props, name, attributes) => {
    const [element, setElement] = useState(null);
    useEffect(() => {
        let el;
        // Spawn the element if it hasn't been yet
        if (!element) {
            el = spawnHeadElement(name);
            setElement(el);
        }
        else {
            el = element;
        }
        // Set element's attributes via
        // set(el, 'content', props.content)
        attributes.forEach((attrib) => {
            setOrRemoveAttribute(el, attrib, props[attrib]);
        });
        // Cleanup by removing the spawned element
        return () => {
            if (el && el.parentNode)
                el.parentNode.removeChild(el);
        };
    }, [JSON.stringify(props)]);
};
/**
 * Component version of useTitle.
 */
const Title = (props) => {
    if (props.title != null) {
        useTitle(props.title);
    }
    else {
        useTitle(nodeToString(props.children));
    }
    return null;
};
/**
 * Component version of useMeta.
 */
const Meta = (props) => {
    useMeta(props);
    return null;
};
/**
 * Component version of useLink.
 */
const Link = (props) => {
    useLink(props);
    return null;
};

export { Link, Meta, Title, useLink, useMeta, useTitle };
